import React from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone } from 'lucide-react';
import { Language } from '../App';
import { PageHero } from '../components/PageHero';
interface ContactProps {
  language: Language;
}
export function Contact({
  language
}: ContactProps) {
  const isPashto = language === 'pashto';
  return <div className="pt-24 md:pt-28" data-id="element-420">
      <PageHero language={language} titleEn="Contact Us" titlePs="اړیکه ونیسئ" introEn="Get in touch with the editors, submit your work, or ask a question." introPs="د مدیرانو سره اړیکه ونیسئ، خپل کارونه راولیږئ، یا پوښتنه وکړئ." data-id="element-421" />

      <section className="py-20 bg-white px-6" data-id="element-422">
        <div className="max-w-5xl mx-auto" data-id="element-423">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-424">
            {/* Contact Info */}
            <div data-id="element-425">
              <h2 className={`text-2xl font-bold text-pasoon-dark mb-6 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-426">
                {isPashto ? 'د اړیکې معلومات' : 'Contact Information'}
              </h2>

              <div className="space-y-6 mb-12" data-id="element-427">
                <div className="flex items-start gap-4" data-id="element-428">
                  <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-429">
                    <Mail className="w-5 h-5 text-pasoon-red" data-id="element-430" />
                  </div>
                  <div data-id="element-431">
                    <h3 className={`font-bold text-pasoon-dark ${isPashto ? 'font-pashto' : ''}`} data-id="element-432">
                      {isPashto ? 'بریښنالیک' : 'Email'}
                    </h3>
                    <p className="text-gray-600" data-id="element-433">pasoonmardan@gmail.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-434">
                  <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-435">
                    <MapPin className="w-5 h-5 text-pasoon-red" data-id="element-436" />
                  </div>
                  <div data-id="element-437">
                    <h3 className={`font-bold text-pasoon-dark ${isPashto ? 'font-pashto' : ''}`} data-id="element-438">
                      {isPashto ? 'پته' : 'Address'}
                    </h3>
                    <p className={`text-gray-600 ${isPashto ? 'font-pashto' : ''}`} data-id="element-439">
                      {isPashto ? 'مردان، خیبر پښتونخوا، پاکستان' : 'Mardan, Khyber Pakhtunkhwa, Pakistan'}
                    </p>
                  </div>
                </div>
              </div>

              <div className={`bg-gray-50 p-6 rounded-xl border border-gray-100 ${isPashto ? 'font-pashto' : ''}`} data-id="element-440">
                <h3 className="font-bold text-pasoon-dark mb-4" data-id="element-441">
                  {isPashto ? 'د لیکوالانو لپاره' : 'For Authors'}
                </h3>
                <ul className="space-y-3 text-sm text-gray-600 list-disc list-inside" data-id="element-442">
                  <li data-id="element-443">
                    {isPashto ? 'مهرباني وکړئ خپل اصلي مقالې او شعرونه راولیږئ' : 'Please send your original articles and poems'}
                  </li>
                  <li data-id="element-444">
                    {isPashto ? 'مقالې باید مخکې په بله مجله یا ورځپاڼه کې نه وي خپرې شوې' : 'The articles shall not be published previously in a magazine or newspaper'}
                  </li>
                  <li data-id="element-445">
                    {isPashto ? 'مهرباني وکړئ د کاغذ په یوه اړخ روښانه ولیکئ' : 'Please write clearly on one side of the paper'}
                  </li>
                  <li data-id="element-446">
                    {isPashto ? 'د پوست یا بریښنالیک له لارې یې راولیږئ' : 'Please send by post or email'}
                  </li>
                  <li data-id="element-447">
                    {isPashto ? 'مهرباني وکړئ د پاسپورټ اندازې عکس راولیږئ که غواړئ چې ستاسو عکس د مقالې تر څنګ کېښودل شي' : 'Please send a passport size photo if you would like to put the photo alongside your article'}
                  </li>
                </ul>
              </div>
            </div>

            {/* Email Us */}
            <div className="bg-white p-8 rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 flex flex-col items-center justify-center text-center" data-id="element-448">
              <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center mb-6" data-id="element-449">
                <Mail className="w-9 h-9 text-pasoon-red" data-id="element-450" />
              </div>

              <h2 className={`text-2xl font-bold text-pasoon-dark mb-3 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-451">
                {isPashto ? 'موږ ته بریښنالیک راولیږئ' : 'Email Us'}
              </h2>

              <p className={`text-gray-600 mb-8 max-w-sm ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-452">
                {isPashto ? 'د پوښتنو، وړاندیزونو یا خپل آثارو لیږلو لپاره موږ ته بریښنالیک راولیږئ.' : 'For enquiries, suggestions, or to submit your work, send us an email.'}
              </p>

              <a href="mailto:pasoonmardan@gmail.com?subject=Website%20Enquiry" className={`w-full flex items-center justify-center gap-3 px-8 py-5 bg-pasoon-red text-white rounded-xl font-bold text-lg hover:bg-red-800 transition-all shadow-lg shadow-red-900/20 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] ${isPashto ? 'font-pashto' : ''}`} data-id="element-453">
                <Mail className="w-5 h-5" data-id="element-454" />
                {isPashto ? 'بریښنالیک راولیږئ' : 'Email Us'}
              </a>

              <p className="text-gray-500 text-sm mt-4" data-id="element-455">
                pasoonmardan@gmail.com
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}